<template>
  <div class="el-main">
    <TaishokutodokeCompanyCard :current-card-id="currentCardId" />

    <TaishokutodokeTextareaCard
      card-id="taishokureason"
      :current-card-id="currentCardId"
      title="退職理由を入力"
      description="退職理由を入力しましょう"
      field-key="taishokureason"
      error-key="reasonSummary"
      :preview-errors="previewErrors"
    />

    <TaishokutodokeTextareaCard
      card-id="entry-detail"
      :current-card-id="currentCardId"
      title="応募経緯"
      description="応募経緯を入力しましょう"
      field-key="entrySummary"
      error-key="entrySummary"
      dialog-name="entry-dialog"
      :show-examples="true"
      :sentences="entrySentences"
      :preview-errors="previewErrors"
    />

    <TaishokutodokeTextareaCard
      card-id="resume-list"
      :current-card-id="currentCardId"
      title="同封書類"
      description="同封書類を入力しましょう"
      field-key="resumeSummary"
      error-key="resumeSummary"
      :preview-errors="previewErrors"
    />

    <TaishokutodokeDateCard
      card-id="retirement-date"
      :current-card-id="currentCardId"
      heading="退職日を入力"
      description="基本は、上司と合意した年月日を書きましょう"
      field="retirementDate"
    />

    <TaishokutodokeDateCard
      card-id="create-date"
      :current-card-id="currentCardId"
      heading="送付状の作成日を入力"
      description="実際に書類を持参する日、または郵送する日を選びましょう"
      field="createDate"
    />

    <LetterConfirmCard :current-card-id="currentCardId" />

    <div
      class="card"
      v-show="currentCardId == 'output'"
      v-if="getMode != 'production1'"
    >
      <DownloadItem
        :formatId="formatId"
        :isSendYagiPostEnabled="isSendYagiPostEnabled"
        :isSendYagiEmailEnabled="isSendYagiEmailEnabled"
        :mailPdfEnabled="mailPdfEnabled"
        :downloadPdfEnabled="downloadPdfEnabled"
        :downloadActiveCollapses="downloadActiveCollapses"
        @update:isSendYagiEmailEnabled="isSendYagiEmailEnabled = $event"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue'
import { emitter } from '@/plugins/emitter'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import { useFormatLocalData } from '@/composables/format-edit/useFormatLocalData'
import {
  createEmptyTaishokutodokeFormatData,
  provideTaishokutodokeFormatData
} from '@/composables/format-edit/useTaishokutodokeFormatData'
import type { LetterExampleSentenceItem } from '@/components/format-edit/letter/LetterExampleSentenceDialog.vue'
import TaishokutodokeCompanyCard from '@/components/format-edit/taishokutodoke/TaishokutodokeCompanyCard.vue'
import TaishokutodokeTextareaCard from '@/components/format-edit/taishokutodoke/TaishokutodokeTextareaCard.vue'
import TaishokutodokeDateCard from '@/components/format-edit/taishokutodoke/TaishokutodokeDateCard.vue'
import LetterConfirmCard from '@/components/format-edit/letter/LetterConfirmCard.vue'
import DownloadItem from '@/components/formats/CommonParts/DownloadItem.vue'

const props = defineProps<{
  basicData: Record<string, unknown>
  formatId?: string
  previewFormat?: { errorMessages?: Record<string, string> } | null
  currentCardId?: string
  exampleSentences?: {
    intro?: LetterExampleSentenceItem[]
    entry?: LetterExampleSentenceItem[]
  }
}>()

const { formLib } = useHostServices()

const { formatData, saveKey, saveName, changeCount } = useFormatLocalData({
  saveName: 'taishokutodoke',
  saveOtherName: 'rirekisho',
  createEmpty: createEmptyTaishokutodokeFormatData
})
provideTaishokutodokeFormatData(formatData)

const mailPdfEnabled = ref(true)
const downloadPdfEnabled = ref(true)
const isSendYagiPostEnabled = ref(true)
const isSendYagiEmailEnabled = ref(true)
const downloadActiveCollapses = ref(['postcard'])

const previewErrors = computed(() => {
  if (!props.previewFormat || !props.previewFormat.errorMessages) return {}
  return props.previewFormat.errorMessages
})

const getMode = computed(() => process.env.VUE_APP_MODE)

const entrySentences = computed(() => props.exampleSentences?.entry || [])

function dataChanged() {
  mailPdfEnabled.value = true
  downloadPdfEnabled.value = true
}

type DateFormLib = {
  getToday: () => Date
  dateFormat: (date: Date, format: string) => string
}

const dateFormLib = formLib as typeof formLib & DateFormLib

watch(
  () => formatData.createDate,
  (newValue) => {
    if (newValue == null) return
    formatData.writeDate = dateFormLib.dateFormat(
      newValue as Date,
      'yyyy年 m月 d日'
    )
  }
)

onBeforeMount(() => {
  if (formatData.createDate === null) {
    formatData.createDate = dateFormLib.getToday()
  }
  if (formatData.retirementDate === null) {
    formatData.retirementDate = dateFormLib.getToday()
  }
  emitter.on('basic-data-changed', dataChanged)
  emitter.on('format-data-changed', dataChanged)
})

onBeforeUnmount(() => {
  emitter.off('basic-data-changed', dataChanged)
  emitter.off('format-data-changed', dataChanged)
})

defineExpose({
  saveName,
  saveKey,
  formatData,
  changeCount
})
</script>

<style lang="scss" scoped>
* {
  list-style: none;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

img {
  width: 100%;
  vertical-align: bottom;
}

.card-header h1,
.card-header h2 {
  padding-bottom: 1.5rem;
}
</style>
